import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

// Define our own hard-coded success stories to avoid API calls
const FEATURED_SUCCESS_STORIES = [
  {
    id: 1,
    title: "From Incarceration to IT Professional",
    clientName: "James M.",
    content: "After 5 years incarcerated, I never thought I'd work in tech. StaffAbility believed in me when no one else would. Now I'm a junior developer with a future I can be proud of.",
    imageUrl: "https://images.unsplash.com/photo-1507537297725-24a1c029d3ca?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
    category: "Employment",
    isPublished: true,
    publishDate: "2024-01-15"
  },
  {
    id: 2,
    title: "Recovery to Restaurant Management",
    clientName: "Maria L.",
    content: "After years of addiction, I found StaffAbility when I was ready to change. They helped me with housing, job training, and placed me with an employer who supported my recovery journey.",
    imageUrl: "https://images.unsplash.com/photo-1573497161161-c3e73707e25c?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
    category: "Recovery",
    isPublished: true,
    publishDate: "2024-02-10"
  },
  {
    id: 3,
    title: "From Homeless to Healthcare Career",
    clientName: "David T.",
    content: "I was living in my car when I found StaffAbility. They helped me complete my GED, get certified as a medical assistant, and find stable housing. I now have a career I love.",
    imageUrl: "https://images.unsplash.com/photo-1560250097-0b93528c311a?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
    category: "Housing",
    isPublished: true,
    publishDate: "2024-03-05"
  },
  {
    id: 4,
    title: "Breaking the Cycle of Recidivism",
    clientName: "Robert J.",
    content: "After multiple incarcerations, I found StaffAbility through my parole officer. Their comprehensive support helped me learn coding, find stable housing, and secure a remote tech job that accommodates my parole restrictions.",
    imageUrl: "https://images.unsplash.com/photo-1543269865-cbf427effbad?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
    category: "Justice Reform",
    isPublished: true,
    publishDate: "2024-04-10"
  },
  {
    id: 5,
    title: "Single Mother to Software Engineer",
    clientName: "Sarah K.",
    content: "As a single mother with a criminal record, I couldn't find work to support my family. StaffAbility provided childcare assistance during my training and helped me build a career in tech that allows me to work remotely and be there for my children.",
    imageUrl: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
    category: "Family Support",
    isPublished: true,
    publishDate: "2024-03-22"
  }
];

// Props interface - we'll keep the API-style props for compatibility but won't use them
interface SuccessStoriesProps {
  stories?: any[];  // We'll ignore this and use hard-coded data
  isLoading?: boolean;
}

export default function SuccessStories({ isLoading = false }: SuccessStoriesProps) {
  // Always show the first 3 stories
  const displayStories = FEATURED_SUCCESS_STORIES.slice(0, 3);

  return (
    <section id="success-stories" className="py-12 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="lg:text-center">
          <h2 className="text-base text-primary-600 font-semibold tracking-wide uppercase">Success Stories</h2>
          <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-slate-900 sm:text-4xl">
            Real people, real transformation
          </p>
          <p className="mt-4 max-w-2xl text-xl text-slate-500 lg:mx-auto">
            Meet some of the individuals who have rebuilt their lives with StaffAbility's support.
          </p>
        </div>

        <div className="mt-10 grid gap-8 md:grid-cols-3">
          {isLoading ? (
            // Loading skeletons
            Array(3).fill(0).map((_, index) => (
              <div key={index} className="bg-white overflow-hidden shadow rounded-lg">
                <Skeleton className="h-48 w-full" />
                <div className="p-6">
                  <Skeleton className="h-6 w-1/2 mb-2" />
                  <Skeleton className="h-4 w-3/4 mb-3" />
                  <Skeleton className="h-4 w-full mb-2" />
                  <Skeleton className="h-4 w-full mb-2" />
                  <Skeleton className="h-4 w-2/3 mb-4" />
                  <Skeleton className="h-4 w-1/3" />
                </div>
              </div>
            ))
          ) : (
            // Success story cards
            displayStories.map((story) => (
              <div key={story.id} className="bg-white overflow-hidden shadow rounded-lg flex flex-col h-full">
                <div className="relative h-64 bg-gray-200 overflow-hidden">
                  <img 
                    className="absolute inset-0 w-full h-full object-cover" 
                    style={{ objectPosition: '50% 30%' }}
                    src={story.imageUrl} 
                    alt={`Success story of ${story.clientName}`} 
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/5"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h3 className="text-lg font-semibold text-white drop-shadow-md">{story.clientName}</h3>
                    <p className="text-sm text-white opacity-90 drop-shadow-md">{story.title}</p>
                  </div>
                </div>
                <div className="p-6 flex-grow">
                  <div className="mb-2 h-1 w-12 bg-primary-500 rounded"></div>
                  <p className="text-slate-600 mb-4">
                    {story.content.length > 150 ? `${story.content.substring(0, 150)}...` : story.content}
                  </p>
                  <Link href={`/success-stories/${story.id}`}>
                    <span className="text-primary-600 hover:text-primary-700 font-medium cursor-pointer">Read {story.clientName.split(' ')[0]}'s story →</span>
                  </Link>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="mt-10 text-center">
          <Link href="/success-stories">
            <Button variant="outline" className="border-slate-300 bg-white text-slate-800 hover:bg-slate-100 shadow-md">
              View All Success Stories
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}
